import React, { useState, useEffect } from "react";
import NameInfo from "./NameInfo.jsx";

export default function Stats({ user }) {
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState(null);
  const [meta, setMeta] = useState(null);
  const [nameInput, setNameInput] = useState('');
  const [view, setView] = useState('accepted');
  const [dragging, setDragging] = useState(null);
  const [dropIndex, setDropIndex] = useState(null);

  const takeClasses = {
    'Love it': "takeLoveit",
    'Like it': "takeLikeit",
    'It\'s ok': "takeItsok",
    'Meh': "takeMeh",
    'WTF': "takeWTF",
  }

  const load = async () => {
    const res = await fetch(`/api/stats/${user.username}?gender=${user.gender}`);
    const d = await res.json();
    setData(d);
  };

  useEffect(() => { load(); }, [user.gender]);

  const addName = async decision => {
    const name = nameInput.trim();
    if (!name) return;
    await fetch(`/api/name/in-or-out/${user.username}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ decision, name })
    });
    setNameInput('');
    load();
  };

  const moveSelected = async () => {
    if (!selected) return;
    const decision = data.accepted.includes(selected) ? 'reject' : 'accept';
    await fetch(`/api/name/in-or-out/${user.username}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ decision, name: selected })
    });
    setSelected(null);
    setMeta(null);
    load();
  };

  const selectName = async n => {
    setSelected(n);
    const res = await fetch(`/api/name/meta/${n}`);
    const m = await res.json();
    setMeta(m);
  };

  const updateRating = async (name, rating) => {
    await fetch(`/api/name/rating/${user.username}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name, rating })
    });
  };

  const handleDrop = async () => {
    if (dragging == null || dropIndex == null) {
      setDragging(null);
      setDropIndex(null);
      return;
    }

    const currentList = list.slice();
    const oldIndex = currentList.indexOf(dragging);
    if (oldIndex === -1) {
      setDragging(null);
      setDropIndex(null);
      return;
    }
    currentList.splice(oldIndex, 1);
    let idx = dropIndex;
    if (idx > oldIndex) idx -= 1;
    currentList.splice(idx, 0, dragging);

    let newRating;
    if (idx === 0) {
      newRating = getRating(currentList[1]) + 10;
    } else if (idx === currentList.length - 1) {
      newRating = getRating(currentList[idx - 1]) - 10;
    } else {
      newRating = (getRating(currentList[idx - 1]) + getRating(currentList[idx + 1])) / 2;
    }
    await updateRating(dragging, Math.round(newRating));
    setDragging(null);
    setDropIndex(null);
    load();
  };

  if (!data) {
    return <div className="loading">Loading your stats...</div>;
  }

  const notes = selected && data.takes[selected] ? data.takes[selected] : [];

  const rankMap = {};
  if (data.ranking) {
    Object.entries(data.ranking)
      .sort((a, b) => {
        const ra = typeof a[1] === 'number' ? a[1] : a[1].rating;
        const rb = typeof b[1] === 'number' ? b[1] : b[1].rating;
        return rb - ra;
      })
      .forEach(([n], i) => { rankMap[n] = i + 1; });
  }

  const getRating = n => {
    const r = data.ranking[n];
    return typeof r === 'number' ? r : (r ? r.rating : 0);
  };

  const list = view === 'accepted'
    ? data.accepted.slice().sort((a, b) => getRating(b) - getRating(a))
    : data.rejected;

  const gender = user.gender == 'male' ? 'Boy' : user.gender == 'female' ? 'Girl' : 'Neutral'

  return (
    <div className="stats-section">
      <div className="card-grid">
        <div className="card">
          <h3>📊 Your Stats</h3>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: '1rem',
              textAlign: 'center'
            }}
          >
            <div>
              <div style={{ fontSize: '2rem', fontWeight: 'bold', color: 'var(--primary-sage)' }}>
                {data.accepted.length}
              </div>
              <div style={{ fontSize: '0.9rem', color: 'var(--text-light)' }}>Loved</div>
            </div>
            <div>
              <div style={{ fontSize: '2rem', fontWeight: 'bold', color: 'var(--primary-coral)' }}>
                {data.rejected.length}
              </div>
              <div style={{ fontSize: '0.9rem', color: 'var(--text-light)' }}>Passed</div>
            </div>
            <div>
              <div style={{ fontSize: '2rem', fontWeight: 'bold', color: 'var(--accent-gold)' }}>
                {Object.keys(data.ranking || {}).length}
              </div>
              <div style={{ fontSize: '0.9rem', color: 'var(--text-light)' }}>Ranked</div>
            </div>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="stats-layout">
          <div className="names-panel">
            <h3 style={{ margin: '0 0 1.5rem 0' }}>📚 Your {gender} Names</h3>
            <div className="toggle-buttons">
              {['accepted', 'rejected'].map(v => (
                <button
                  key={v}
                  className={`toggle-btn ${view === v ? 'active' : ''}`}
                  onClick={() => {
                    setView(v);
                    setSelected(null);
                    setMeta(null);
                  }}
                >
                  {v === 'accepted' ? '👍 Liked' : '👎 Passed'}
                </button>
              ))}
            </div>

            {list.length > 0 ? (
              <div className="table-scroll">
              <table className="name-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    {view === 'accepted' && <th>Your Rating</th>}
                    {view === 'accepted' && <th>Your Rank</th>}
                    <th>Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {list.map((n, i) => (
                    <tr
                      key={n}
                      draggable={view === 'accepted'}
                      onDragStart={() => setDragging(n)}
                      onDragEnd={handleDrop}
                      onDragOver={e => {
                        if (view !== 'accepted') return;
                        e.preventDefault();
                        const rect = e.currentTarget.getBoundingClientRect();
                        const before = e.clientY - rect.top < rect.height / 2;
                        setDropIndex(before ? i : i + 1);
                      }}
                      onDrop={e => { e.preventDefault(); handleDrop(); }}
                      className={`${selected === n ? 'selected' : ''} ${dragging === n ? 'dragging' : ''} ${dropIndex === i ? 'drop-target' : ''}`}
                      onClick={() => selectName(n)}
                    >
                      <td>
                        <strong>{n}</strong>
                      </td>
                      {view === 'accepted' && <td>{Math.round(getRating(n))}</td>}
                      {view === 'accepted' && <td>#{rankMap[n] || '-'}</td>}
                      <td>{data.takes[n] ? data.takes[n].length : 0}</td>
                    </tr>
                  ))}
                  {dragging && (
                    <tr
                      className={dropIndex === list.length ? 'drop-target' : ''}
                      style={{ height: '4px' }}
                      onDragOver={e => { e.preventDefault(); setDropIndex(list.length); }}
                      onDrop={e => { e.preventDefault(); handleDrop(); }}
                    />
                  )}
                </tbody>
              </table>
              </div>
            ) : (
              <div className="no-data">
                {view === 'accepted'
                  ? "No favorites yet! Visit 'In or Out' to start building your list."
                  : "No rejected names yet. That's okay - be picky!"}
              </div>
            )}

            <div style={{ marginTop: '1.5rem' }}>
              <h4>➕ Add Name Manually</h4>
              <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
                <input
                  placeholder="Enter a name..."
                  value={nameInput}
                  onChange={e =>
                    setNameInput(
                      e.target.value.replace(/^./, c => c.toUpperCase())
                    )
                  }
                  style={{ flex: 1 }}
                />
              </div>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button
                  className="btn-secondary"
                  onClick={() => addName('accept')}
                  style={{ flex: 1 }}
                >
                  👍 Add to Liked
                </button>
                <button
                  className="btn-secondary"
                  onClick={() => addName('reject')}
                  style={{ flex: 1 }}
                >
                  👎 Add to Passed
                </button>
              </div>
            </div>
          </div>

          {selected && (
            <div className="name-details">
              <button
                onClick={moveSelected}
                className={data.accepted.includes(selected) ? 'btn-outline' : 'btn-primary'}
                style={{ width: '100%', marginTop: '1rem' }}
              >
                {data.accepted.includes(selected) ? '👎 Move to Passed' : '👍 Move to Liked'}
              </button>
              {notes.length > 0 && (
                <div style={{ marginTop: '1.5rem' }}>
                  <h4>💬 Friends & Family Takes</h4>
                  <div
                    style={{
                      background: 'var(--primary-cream)',
                      padding: '1rem',
                      borderRadius: 'var(--border-radius-small)'
                    }}
                  >
                    {notes.map((t, i) => (
                      <div
                        key={i}
                        style={{
                          marginBottom: '0.75rem',
                          padding: '0.75rem',
                          background: 'var(--white)',
                          borderRadius: 'var(--border-radius-small)',
                          borderLeft: '4px solid var(--primary-sage)'
                        }}
                      >
                        {/* <strong>{`${t.from}: `}</strong> */}
                        <span className={takeClasses[t.take]}>{t.take}</span>
                        {t.note && (
                          <div
                            style={{
                              marginTop: '0.25rem',
                              fontStyle: 'italic',
                              color: 'var(--text-light)'
                            }}
                          >
                            "{t.note}"
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <NameInfo name={selected} meta={meta} gender={user.gender} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
